import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';

/**
 * Configuración para una polyline
 */
export interface PolylineConfig {
  id?: string;
  points: [number, number][];
  color?: string;
  width?: number;
  opacity?: number;
  dash?: boolean;
  dashArray?: number[];
  centrar?: boolean; // Si es true, el mapa se centra automáticamente en esta polyline
}

/**
 * PolylinesLayerManager
 *
 * Manager especializado para renderizar polylines (líneas) en el mapa.
 *
 * Características:
 * - Múltiples polylines simultáneas
 * - Configuración individual de color, ancho, estilo
 * - Soporte para líneas punteadas (dash)
 * - NO usa el patrón de EntityLayerManager (geometrías no son entidades puntuales)
 *
 * Uso típico:
 * - Recorridos de vehículos
 * - Rutas de colectivos
 * - Trayectorias
 */
@Injectable({
  providedIn: 'root',
})
export class PolylinesLayerManager {
  private map: mapboxgl.Map | null = null;
  private sourceId = 'polylines-source';
  private layerId = 'polylines-layer';
  private isInitialized = false;

  // ========== LIFECYCLE ==========

  initialize(map: mapboxgl.Map): void {
    if (this.isInitialized) {
      console.warn('⚠️ PolylinesLayerManager ya está inicializado');
      return;
    }

    this.map = map;
    this.createSourceAndLayer();
    this.isInitialized = true;
  }

  cleanup(): void {
    if (!this.map) return;

    if (this.map.getLayer(this.layerId)) {
      this.map.removeLayer(this.layerId);
    }

    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }

    this.map = null;
    this.isInitialized = false;
  }

  // ========== CREACIÓN ==========

  private createSourceAndLayer(): void {
    if (!this.map) return;

    // Source para polylines
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
        buffer: 0, // OPTIMIZACIÓN: Sin buffer para mejor performance
        maxzoom: 16, // OPTIMIZACIÓN: Limitar zoom máximo
        tolerance: 0.375, // Default, puede incrementarse para simplificar geometrías
      });
    }

    // Layer para polylines
    if (!this.map.getLayer(this.layerId)) {
      const layer: mapboxgl.LineLayer = {
        id: this.layerId,
        type: 'line',
        source: this.sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['get', 'width'],
          'line-opacity': ['coalesce', ['get', 'opacity'], 1],
          'line-dasharray': [
            'case',
            ['get', 'dash'],
            ['literal', [2, 2]], // Dash pattern si dash === true
            ['literal', [1, 0]], // Sólido si dash === false
          ],
          // SOLUCIÓN: line-emissive-strength hace que las líneas "brillen"
          // sin verse afectadas por el esquema de iluminación (lightPreset)
          // Valor por defecto es 0, valores > 0 hacen que el color sea más vibrante
          'line-emissive-strength': 0.8,
        } as any, // Paint acepta propiedades experimentales no en el tipo oficial
      };
      this.map.addLayer(layer);
    }
  }

  // ========== DATOS ==========

  /**
   * Actualiza las polylines en el mapa
   */
  updatePolylines(polylines: PolylineConfig[]): void {
    if (!this.map || !this.isInitialized) {
      console.warn('⚠️ PolylinesLayerManager no está inicializado');
      return;
    }

    const features = polylines.map((polyline, index) => {
      return this.polylineToFeature(polyline, index);
    });

    const geojson: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
      type: 'FeatureCollection',
      features,
    };

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(geojson);
    }

    // Centrar en polylines si tienen centrar: true
    const polylinesToCenter = polylines.filter((p) => p.centrar === true);
    if (polylinesToCenter.length > 0 && this.map) {
      if (polylinesToCenter.length === 1) {
        const polyline = polylinesToCenter[0];
        if (polyline.points && polyline.points.length >= 2) {
          const coordinates = polyline.points.map((p) => [p[0], p[1]]);
          const bounds = coordinates.reduce(
            (bounds, coord) => bounds.extend(coord as [number, number]),
            new mapboxgl.LngLatBounds(
              coordinates[0] as [number, number],
              coordinates[0] as [number, number],
            ),
          );
          this.map.fitBounds(bounds, {
            padding: 50,
            duration: 1000,
          });
        }
      } else {
        const allPoints = polylinesToCenter.flatMap((p) => p.points || []);
        if (allPoints.length >= 2) {
          const coordinates = allPoints.map((p) => [p[0], p[1]]);
          const bounds = coordinates.reduce(
            (bounds, coord) => bounds.extend(coord as [number, number]),
            new mapboxgl.LngLatBounds(
              coordinates[0] as [number, number],
              coordinates[0] as [number, number],
            ),
          );
          this.map.fitBounds(bounds, {
            padding: 50,
            duration: 1000,
          });
        }
      }
    }
  }

  /**
   * Limpia todas las polylines
   */
  clearPolylines(): void {
    if (!this.map || !this.isInitialized) return;

    const emptyGeoJSON: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
      type: 'FeatureCollection',
      features: [],
    };

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(emptyGeoJSON);
    }
  }

  // ========== CONVERSIÓN ==========

  private polylineToFeature(
    polyline: PolylineConfig,
    index: number,
  ): GeoJSON.Feature<GeoJSON.LineString> {
    return {
      type: 'Feature',
      id: polyline.id || `polyline-${index}`,
      geometry: {
        type: 'LineString',
        coordinates: polyline.points,
      },
      properties: {
        color: polyline.color || '#3bb2d0',
        width: polyline.width || 2,
        opacity: polyline.opacity !== undefined ? polyline.opacity : 1,
        dash: polyline.dash || false,
      },
    };
  }

  // ========== VISIBILIDAD ==========

  setVisibility(visible: boolean): void {
    if (!this.map) return;

    if (this.map.getLayer(this.layerId)) {
      this.map.setLayoutProperty(
        this.layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  getVisibility(): boolean {
    if (!this.map || !this.map.getLayer(this.layerId)) return false;
    return this.map.getLayoutProperty(this.layerId, 'visibility') === 'visible';
  }
}
